import { useState, useEffect } from "react";
import GuidelineLiveness from "./components/GuidelineLiveness";
import Liveness from "./components/Liveness";
import { useLocation } from "react-router-dom";
import InvalidToken from "./components/InvalidToken";
import ExceedMaxRetry from "./components/ExceedMaxRetry";
import LivenessReject from "./components/LivenessReject";

const FaceLiveness = ({ validation, onComplete, onSessionUpdate }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token") || sessionStorage.getItem("ekycSessionToken");

  const [step, setStep] = useState("guideline-liveness");

  const [progressStates, setProgressStates] = useState({
    liveness: "Not Started",
  });

  const [baseValidationCount, setBaseValidationCount] = useState(0);
  const [attemptId, setAttemptId] = useState(null);

  const [livenessError, setLivenessError] = useState({
    errorMessage: "",
  });

  const [faceLivenessUIConfig, setFaceLivenessUIConfig] = useState({});
  const maxRetries =
    validation?.WorkflowConfigSnapshot?.Components?.[validation?.CurrentStep?.ID]
      ?.MaxRetries ?? 1;

  useEffect(() => {
    if (validation) {
      const faceLivenessResult =
        validation?.ComponentResults?.[validation.CurrentStep.ID] || {};
      setProgressStates({
        liveness: faceLivenessResult?.Result
          ? faceLivenessResult.Result
          : "Not Started",
      });
      const newBaseCount = faceLivenessResult?.VALIDATION_COUNT || 0;
      setBaseValidationCount(newBaseCount);
    }
  }, [validation]);

  useEffect(() => {
    const overallResult =
      validation?.ComponentResults?.[validation.CurrentStep.ID]?.Result;
    if (
      baseValidationCount > maxRetries &&
      overallResult !== "Pass" &&
      overallResult !== "In Review"
    ) {
      setStep("exceed-max-retry");
      return;
    }

    if (progressStates.liveness === "Pass") {
      // Only auto-complete if liveness already passed
      onComplete({ validationID: validation?.ValidationID });
    } else if (
      step === "liveness-rejected" ||
      step === "exceed-max-retry" ||
      step === "take-liveness"
    ) {
      return;
    } else {
      // "Not Started", "Fail", or any other status - show guideline
      setStep("guideline-liveness");
    }

    // Extract UI config from validation
    const uiConfig = validation?.WorkflowConfigSnapshot?.Config?.[
      validation?.CurrentStep?.ID
    ]?.FaceLivenessUIConfig || {};
    setFaceLivenessUIConfig(uiConfig);
  }, [
    progressStates,
    baseValidationCount,
    validation,
    onComplete,
    step,
  ]);

  const renderSteps = () => {
    switch (step) {
      case "guideline-liveness":
        return (
          <GuidelineLiveness
            onNext={() => {
              setAttemptId(`${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
              setStep("take-liveness");
            }}
            uiConfig={faceLivenessUIConfig}
          />
        );
      case "take-liveness":
        return (
          <Liveness
            validationID={validation?.ValidationID}
            currentStepID={validation?.CurrentStep.ID}
            attemptId={attemptId}
            onSessionUpdate={onSessionUpdate}
            onSuccess={(validationID) =>
              onComplete({ validationID: validationID })
            }
            onValidationCount={(count) => {
              if (Number.isFinite(count)) {
                setBaseValidationCount(count);
              }
            }}
            onFail={(error) => {
              setLivenessError({
                errorMessage: error.failMessage,
              });
              if (Number.isFinite(error.validationCount)) {
                setBaseValidationCount(error.validationCount);
              }
              if (error.isRetryLimit) {
                setStep("exceed-max-retry");
              } else {
                setStep("liveness-rejected");
              }
            }}
          />
        );
      case "liveness-rejected":
        return (
          <LivenessReject
            errorMessage={livenessError.errorMessage}
            onRetry={() => {
              if (baseValidationCount > maxRetries) {
                setStep("exceed-max-retry");
                return;
              }
              setLivenessError({ errorMessage: "" });
              setAttemptId(`${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
              setStep("take-liveness");
            }}
            uiConfig={faceLivenessUIConfig}
          />
        );
      case "exceed-max-retry":
        return <ExceedMaxRetry />;
      case "invalid-token":
        return <InvalidToken />;
      default:
        // Return loading screen
        return <div>Loading...</div>;
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex justify-center items-center">
      <div className="min-h-screen w-full max-w-md bg-transparent relative md:rounded-xl md:shadow-lg">
        {!token ? <InvalidToken /> : renderSteps()}
      </div>
    </div>
  );
};

export default FaceLiveness;
